import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AppHeader from "../components/AppHeader";
import PullToRefresh from "../components/PullToRefresh";
import {
    authApi,
    checklistApi,
    getStaff,
    normalizeProfile,
} from "../utils/Api";
import {
    formatBusinessDate,
    formatDateTime,
    FREQUENCY_HINT,
    labelize,
    normalizeBusinessDate,
} from "../utils/format";
import { captureCurrentLocation, getCachedLocation } from "../utils/location";

const STATUS_CONFIG = {
    SUBMITTED: {
        label: "SUBMITTED",
        cardClass:
            "bg-gradient-to-br from-white via-green-50 to-emerald-50 border border-green-100 shadow-[0_18px_45px_rgba(34,197,94,0.13)]",
        decorationClass: "bg-green-100/70",
        badgeClass: "bg-green-100 text-green-600",
        statusTextClass: "text-green-600",
        color: "#22C55E",
    },
    DRAFT: {
        label: "DRAFT",
        cardClass:
            "bg-gradient-to-br from-white via-sky-50 to-cyan-50 border border-sky-100 shadow-[0_18px_45px_rgba(14,165,233,0.13)]",
        decorationClass: "bg-sky-100/70",
        badgeClass: "bg-sky-100 text-sky-600",
        statusTextClass: "text-sky-600",
        color: "#0EA5E9",
    },
    PENDING: {
        label: "PENDING",
        cardClass:
            "bg-white border border-[#D7EEF6] shadow-[0_18px_45px_rgba(14,73,97,0.11)] hover:shadow-[0_24px_55px_rgba(14,73,97,0.16)]",
        decorationClass: "bg-[#E9F7FC]/80",
        badgeClass: "bg-amber-100 text-amber-600",
        statusTextClass: "text-[#6E8794]",
        color: "#F59E0B",
    },
    MISSED: {
        label: "MISSED",
        cardClass:
            "bg-gradient-to-br from-white via-red-50 to-rose-50 border border-red-100 shadow-[0_18px_45px_rgba(239,68,68,0.12)] opacity-90",
        decorationClass: "bg-red-100/70",
        badgeClass: "bg-red-100 text-red-600",
        statusTextClass: "text-red-500",
        color: "#EF4444",
    },
};

const GROUP_META = {
    OPENING: {
        title: "Opening",
        subtitle: "Same day, 12:00 AM to 1:00 PM",
        icon: "☀️",
    },
    CLOSING: {
        title: "Closing",
        subtitle: "8:00 PM until 5:00 AM next day",
        icon: "🌙",
    },
    GENERAL: {
        title: "General",
        subtitle: "Sundays, 2nd Sunday, and month end",
        icon: "📋",
    },
};

const TYPE_ORDER = { OPENING: 1, CLOSING: 2, GENERAL: 3 };

function groupChecklists(rows) {
    const grouped = rows.reduce((acc, item) => {
        const type = item.checklist_type || "OTHER";

        if (!acc[type]) acc[type] = [];
        acc[type].push(item);

        return acc;
    }, {});

    return Object.entries(grouped)
        .sort(
            ([typeA], [typeB]) =>
                (TYPE_ORDER[typeA] || 99) - (TYPE_ORDER[typeB] || 99)
        )
        .map(([type, categories]) => {
            const meta = GROUP_META[type] || {
                title: labelize(type) || "Checklist",
                subtitle: "Assigned outlet checklist",
                icon: "📋",
            };

            return {
                id: type,
                groupTitle: meta.title,
                groupSubtitle: meta.subtitle,
                icon: meta.icon,
                items: [...categories]
                    .sort(
                        (a, b) =>
                            Number(a.sort_order || 0) - Number(b.sort_order || 0)
                    )
                    .map((category) => ({
                        categoryId: category.category_id,
                        title: category.category_name,
                        taskCount: Number(category.task_count || 0),
                        frequency: category.frequency || "DAILY",
                        checklistType: category.checklist_type || type,
                        businessDate: normalizeBusinessDate(category.business_date),
                        status: String(category.status || "PENDING").toUpperCase().trim(),
                        readOnly: Boolean(category.read_only),
                        availableUntil: category.available_until || null,
                        submittedBy: category.submitted_by || null,
                        submittedAt: category.submitted_at || null,
                        isOwnSubmission: Boolean(category.is_own_submission),
                    })),
            };
        });
}

const ProgressCircle = ({ percent, color }) => {
    const safePercent = Math.min(Math.max(percent || 0, 0), 100);
    const radius = 24;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (safePercent / 100) * circumference;

    return (
        <div className="relative w-[66px] h-[66px] shrink-0">
            <svg className="w-[66px] h-[66px] -rotate-90">
                <circle
                    cx="33"
                    cy="33"
                    r={radius}
                    stroke="#374151"
                    strokeWidth="7"
                    fill="none"
                />
                <circle
                    cx="33"
                    cy="33"
                    r={radius}
                    stroke={color}
                    strokeWidth="7"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    className="transition-all duration-500"
                />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-[13px] font-extrabold text-white">
                    {safePercent}%
                </span>
            </div>
        </div>
    );
};

const StatusIndicator = ({ status, taskCount }) => {
    if (status === "SUBMITTED") {
        return (
            <div className="w-[72px] h-[72px] rounded-[24px] shrink-0 flex flex-col items-center justify-center bg-gradient-to-br from-green-50 to-emerald-100 border border-green-100">
                <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center text-lg font-black shadow-[0_8px_18px_rgba(34,197,94,0.28)]">
                    ✓
                </div>
                <span className="text-[9px] font-extrabold text-green-600 mt-1">
                    DONE
                </span>
            </div>
        );
    }

    if (status === "DRAFT") {
        return (
            <div className="w-[72px] h-[72px] rounded-[24px] shrink-0 flex flex-col items-center justify-center bg-gradient-to-br from-sky-50 to-cyan-100 border border-sky-100">
                <div className="w-8 h-8 rounded-full bg-sky-500 text-white flex items-center justify-center text-sm font-black">
                    ✎
                </div>
                <span className="text-[9px] font-extrabold text-sky-600 mt-1">
                    DRAFT
                </span>
            </div>
        );
    }

    if (status === "MISSED") {
        return (
            <div className="w-[72px] h-[72px] rounded-[24px] shrink-0 flex flex-col items-center justify-center bg-gradient-to-br from-red-50 to-rose-100 border border-red-100">
                <div className="w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center text-lg font-black shadow-[0_8px_18px_rgba(239,68,68,0.25)]">
                    !
                </div>
                <span className="text-[9px] font-extrabold text-red-600 mt-1">
                    MISSED
                </span>
            </div>
        );
    }

    return (
        <div className="w-[72px] h-[72px] rounded-[24px] shrink-0 flex flex-col items-center justify-center bg-[#F7FBFD] border border-[#DDF0F6]">
            <span className="text-[22px] font-black leading-none text-amber-500">
                {taskCount || 0}
            </span>
            <span className="text-[9px] font-extrabold text-[#6E8794] mt-1">
                TASKS
            </span>
        </div>
    );
};

export default function Home() {
    const navigate = useNavigate();

    const [showLocationModal, setShowLocationModal] = useState(false);
    const [locationName, setLocationName] = useState("");
    const [locationStatus, setLocationStatus] = useState("idle");
    const [greeting, setGreeting] = useState("");
    const [checklistGroups, setChecklistGroups] = useState([]);
    const [checklistLoading, setChecklistLoading] = useState(true);
    const [checklistError, setChecklistError] = useState("");
    const [profile, setProfile] = useState(() => getStaff());

    const loadDashboard = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setChecklistLoading(true);

        try {
            const json = await checklistApi.available();
            const rows = Array.isArray(json?.data?.checklists)
                ? json.data.checklists
                : [];
            setChecklistGroups(groupChecklists(rows));
            setChecklistError("");
        } catch (error) {
            if (error?.status === 401) return;

            const message = error?.message || "Unable to load checklists";
            setChecklistError(message);

            if (!silent) setChecklistGroups([]);
        } finally {
            setChecklistLoading(false);
        }

        try {
            const me = await authApi.me();
            const nextProfile = normalizeProfile(me?.data);

            if (nextProfile) {
                setProfile(nextProfile);
                localStorage.setItem("staff", JSON.stringify(nextProfile));

                if (nextProfile.outlet) {
                    localStorage.setItem(
                        "outlet",
                        JSON.stringify(nextProfile.outlet)
                    );
                }
            }
        } catch (error) {
            if (error?.status === 401) return;
        }
    }, []);

    useEffect(() => {
        const hour = new Date().getHours();

        if (hour < 12) setGreeting("Good Morning!");
        else if (hour < 17) setGreeting("Good Afternoon!");
        else if (hour < 21) setGreeting("Good Evening!");
        else setGreeting("Good Night!");
    }, []);

    useEffect(() => {
        const cachedLocation = getCachedLocation();

        if (cachedLocation) {
            setLocationName(cachedLocation.name || "Location captured");
            setLocationStatus("allowed");
            setShowLocationModal(false);
            return;
        }

        setShowLocationModal(true);
        requestLocation();
    }, []);

    useEffect(() => {
        loadDashboard();
    }, [loadDashboard]);

    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState === "visible") {
                loadDashboard({ silent: true });
            }
        };

        document.addEventListener("visibilitychange", onVisible);
        return () => document.removeEventListener("visibilitychange", onVisible);
    }, [loadDashboard]);

    const allItems = useMemo(
        () => checklistGroups.flatMap((group) => group.items),
        [checklistGroups]
    );

    const checklistStats = useMemo(() => {
        const total = allItems.length;
        const submitted = allItems.filter((item) => item.status === "SUBMITTED").length;
        const pending = allItems.filter((item) => item.status === "PENDING").length;
        const draft = allItems.filter((item) => item.status === "DRAFT").length;
        const missed = allItems.filter((item) => item.status === "MISSED").length;
        const pendingTasks = allItems
            .filter((item) => item.status === "PENDING")
            .reduce((sum, item) => sum + Number(item.taskCount || 0), 0);
        const submittedTasks = allItems
            .filter((item) => item.status === "SUBMITTED")
            .reduce((sum, item) => sum + Number(item.taskCount || 0), 0);

        return {
            total,
            submitted,
            pending,
            draft,
            missed,
            pendingTasks,
            submittedTasks,
            completionPercent:
                total > 0 ? Math.round((submitted / total) * 100) : 0,
        };
    }, [allItems]);

    const requestLocation = async () => {
        setLocationStatus("loading");

        try {
            const captured = await captureCurrentLocation();
            setLocationName(captured.name || "Location captured");
            setLocationStatus("allowed");

            setTimeout(() => {
                setShowLocationModal(false);
            }, 1400);
        } catch (error) {
            const message = error?.message || "";

            if (message.toLowerCase().includes("support")) {
                setLocationStatus("unsupported");
                return;
            }

            setLocationStatus("denied");
        }
    };

    const getChecklistStatusMessage = (item) => {
        if (item.status === "SUBMITTED") {
            return item.submittedAt
                ? `Submitted on ${formatDateTime(item.submittedAt)}`
                : "Checklist submitted";
        }

        if (item.status === "MISSED") {
            return item.availableUntil
                ? `Window closed ${formatDateTime(item.availableUntil)}`
                : "Submission window has closed";
        }

        if (item.status === "DRAFT") {
            return item.availableUntil
                ? `Draft open until ${formatDateTime(item.availableUntil)}`
                : "Draft saved. Continue to finish this category.";
        }

        return item.availableUntil
            ? `Open until ${formatDateTime(item.availableUntil)}`
            : "Ready to start";
    };

    const openChecklist = (item) => {
        if (item.status === "MISSED") return;

        if (
            !item.categoryId ||
            !item.checklistType ||
            !item.frequency ||
            !item.businessDate
        ) {
            toast.error(
                "This checklist cannot be opened because some details are missing."
            );
            return;
        }

        const params = new URLSearchParams({
            category_id: String(item.categoryId),
            checklist_type: item.checklistType,
            frequency: item.frequency,
            business_date: item.businessDate,
        });

        navigate(`/checklist?${params.toString()}`);
    };

    return (
        <main className="min-h-screen bg-gradient-to-b from-[#18c2dc] via-[#08a8cc] to-[#017fa8] flex justify-center font-sans">
            <section className="relative w-full max-w-[430px] min-h-screen bg-[#F6FBFD] shadow-[0_24px_70px_rgba(14,73,97,0.26)] overflow-hidden">
                {showLocationModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center px-5 bg-[#082F3D]/50 backdrop-blur-md">
                        <div className="w-full max-w-[380px] rounded-[32px] bg-white shadow-[0_28px_80px_rgba(0,0,0,0.28)] overflow-hidden">
                            <div className="relative bg-gradient-to-br from-[#09A5F7] to-[#017FA8] px-6 pt-7 pb-10 text-white">
                                <div className="absolute -right-10 -top-10 w-32 h-32 rounded-full bg-white/10" />
                                <div className="absolute -left-8 -bottom-10 w-28 h-28 rounded-full bg-white/10" />

                                <div className="relative z-10 flex items-center gap-4">
                                    <div className="w-16 h-16 rounded-[24px] bg-white/20 backdrop-blur flex items-center justify-center text-3xl shadow-inner flex-shrink-0">
                                        📍
                                    </div>
                                    <div>
                                        <h2 className="text-2xl font-extrabold">
                                            Location Access
                                        </h2>
                                        <p className="text-white/85 text-sm mt-1 leading-relaxed">
                                            GPS location is required before a
                                            checklist can be saved or submitted.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="px-6 py-6">
                                {locationStatus === "loading" && (
                                    <div className="flex items-center gap-3 rounded-[20px] bg-[#F1FAFD] border border-[#DFEEF4] p-4">
                                        <div className="w-9 h-9 rounded-full border-[3px] border-[#09A5F7]/20 border-t-[#09A5F7] animate-spin" />
                                        <div>
                                            <p className="text-[#0E4961] font-extrabold text-sm">
                                                Fetching your location
                                            </p>
                                            <p className="text-[#6E8794] text-xs mt-0.5">
                                                Please allow permission from the
                                                browser popup
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {locationStatus === "allowed" && (
                                    <div className="rounded-[22px] bg-green-50 border border-green-100 p-4">
                                        <p className="text-green-600 font-extrabold text-sm">
                                            Location captured successfully
                                        </p>
                                        <div className="mt-3 rounded-[18px] bg-white p-4">
                                            <p className="text-[10px] uppercase tracking-wide text-[#6E8794] font-bold">
                                                Current Location
                                            </p>
                                            <p className="text-[#0E4961] text-sm font-extrabold mt-1 leading-relaxed">
                                                {locationName || "Location captured"}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {locationStatus === "denied" && (
                                    <div>
                                        <div className="rounded-[22px] bg-red-50 border border-red-100 p-4">
                                            <p className="text-red-500 font-extrabold text-sm">
                                                Location permission denied
                                            </p>
                                            <p className="text-[#6E8794] text-xs mt-1 leading-relaxed">
                                                You can still view checklists.
                                                Saving or submitting needs
                                                location to be enabled.
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={requestLocation}
                                            className="mt-4 w-full h-12 rounded-[18px] bg-[#09A5F7] text-white font-extrabold shadow-[0_14px_30px_rgba(9,165,247,0.28)] active:scale-[0.98] transition"
                                        >
                                            Try Again
                                        </button>
                                    </div>
                                )}

                                {locationStatus === "unsupported" && (
                                    <div className="rounded-[22px] bg-orange-50 border border-orange-100 p-4">
                                        <p className="text-orange-500 font-extrabold text-sm">
                                            Location not supported
                                        </p>
                                        <p className="text-[#6E8794] text-xs mt-1">
                                            This browser does not support
                                            geolocation, so checklists cannot be
                                            submitted from here.
                                        </p>
                                    </div>
                                )}

                                {locationStatus !== "loading" && (
                                    <button
                                        type="button"
                                        onClick={() => setShowLocationModal(false)}
                                        className="mt-4 w-full h-12 rounded-[18px] bg-[#F1FAFD] text-[#0E4961] font-extrabold border border-[#DFEEF4] active:scale-[0.98] transition"
                                    >
                                        Continue
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <AppHeader />

                <PullToRefresh onRefresh={() => loadDashboard({ silent: true })}>
                <div className="px-5 pt-5 pb-9">
                    <div className="mb-6">
                        <h1 className="text-[24px] font-extrabold text-[#0E4961] leading-tight mt-1">
                            Hi, {profile?.staff_name || "Staff"}
                            <span className="text-[18px] px-2 font-medium text-[#6E8794]">
                                {greeting} 👋
                            </span>
                        </h1>

                        {profile?.outlet && (
                            <div className="flex items-start gap-2 mt-2">
                                <span className="text-sm mt-0.5">📍</span>
                                <p className="text-sm font-medium text-[#6E8794]">
                                    {[
                                        profile.outlet.outlet_name,
                                        profile.outlet.state,
                                    ]
                                        .filter(Boolean)
                                        .join(", ")}
                                </p>
                            </div>
                        )}

                        {locationName && (
                            <div className="mt-3 rounded-[18px] bg-white border border-[#DFEEF4] px-4 py-3 shadow-sm">
                                <p className="text-[11px] uppercase tracking-wide text-[#6E8794] font-extrabold">
                                    Current Location
                                </p>
                                <p className="text-xs text-[#0E4961] font-bold mt-1 leading-relaxed">
                                    {locationName}
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-[1.6fr_1fr] gap-3 mt-5">
                        <div className="flex flex-col gap-3">
                            <div className="relative overflow-hidden rounded-[22px] bg-gradient-to-r from-[#7C4DFF] to-[#B45CFF] px-4 py-3 text-white shadow-lg">
                                <div className="absolute -right-6 -top-6 w-20 h-20 rounded-full bg-white/10" />
                                <div className="flex items-center gap-3 relative z-10">
                                    <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-md">
                                        <span className="text-[#7C4DFF] text-xl">⚡</span>
                                    </div>
                                    <div>
                                        <h3 className="text-3xl font-bold leading-none">
                                            {checklistStats.pending}
                                        </h3>
                                        <p className="text-sm font-semibold">Pending</p>
                                        <p className="text-[11px] text-white/80">
                                            {checklistStats.pendingTasks} tasks awaiting submission
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="relative overflow-hidden rounded-[22px] bg-gradient-to-r from-[#09A5F7] to-[#4E8DFF] px-4 py-3 text-white shadow-lg">
                                <div className="absolute -right-6 -top-6 w-20 h-20 rounded-full bg-white/10" />
                                <div className="flex items-center gap-3 relative z-10">
                                    <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-md">
                                        <span className="text-[#09A5F7] text-xl">✓</span>
                                    </div>
                                    <div>
                                        <h3 className="text-3xl font-bold leading-none">
                                            {checklistStats.submitted}
                                        </h3>
                                        <p className="text-sm font-semibold">Submitted</p>
                                        <p className="text-[11px] text-white/80">
                                            {checklistStats.submittedTasks} tasks completed
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="relative overflow-hidden rounded-[24px] bg-gradient-to-br from-[#2D2D2D] via-[#181818] to-black p-4 text-white min-h-[190px] flex flex-col items-center justify-center text-center">
                            <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/5" />
                            <ProgressCircle
                                percent={checklistStats.completionPercent}
                                color="#FBB400"
                            />
                            <div className="mt-2">
                                <h3 className="text-xl font-bold leading-tight">
                                    Today&apos;s Work
                                </h3>
                                <p className="text-white/70 text-sm mt-1">
                                    {checklistStats.submitted} / {checklistStats.total} Done
                                </p>
                                {checklistStats.draft > 0 && (
                                    <p className="text-sky-300 text-[11px] font-bold mt-1">
                                        {checklistStats.draft} draft
                                        {checklistStats.draft === 1 ? "" : "s"}
                                    </p>
                                )}
                                {checklistStats.missed > 0 && (
                                    <p className="text-red-300 text-[11px] font-bold mt-1">
                                        {checklistStats.missed} missed
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="mt-8 flex items-end justify-end gap-3">
                        <button
                            type="button"
                            onClick={() => navigate("/history")}
                            className="text-[#09A5F7] text-sm font-extrabold"
                        >
                            History
                        </button>
                    </div>

                    {checklistError && checklistGroups.length > 0 && (
                        <div className="mt-3 rounded-[18px] bg-red-50 border border-red-100 px-4 py-3">
                            <p className="text-xs font-bold text-red-500">
                                {checklistError}
                            </p>
                        </div>
                    )}

                    <div className="mt-4 space-y-6">
                        {checklistLoading ? (
                            <div className="rounded-[24px] bg-white border border-[#D7EEF6] p-5 text-center shadow-[0_18px_45px_rgba(14,73,97,0.11)]">
                                <div className="w-9 h-9 mx-auto rounded-full border-[3px] border-[#09A5F7]/20 border-t-[#09A5F7] animate-spin" />
                                <p className="mt-3 text-sm font-extrabold text-[#0E4961]">
                                    Loading checklists...
                                </p>
                            </div>
                        ) : checklistError && checklistGroups.length === 0 ? (
                            <div className="rounded-[24px] bg-red-50 border border-red-100 p-5 text-center shadow-[0_18px_45px_rgba(239,68,68,0.1)]">
                                <p className="text-sm font-extrabold text-red-500">
                                    {checklistError}
                                </p>
                                <button
                                    type="button"
                                    onClick={() => loadDashboard()}
                                    className="mt-4 px-5 h-10 rounded-[14px] bg-red-500 text-white text-xs font-extrabold"
                                >
                                    Try Again
                                </button>
                            </div>
                        ) : checklistGroups.length === 0 ? (
                            <div className="rounded-[24px] bg-white border border-[#D7EEF6] p-5 text-center shadow-[0_18px_45px_rgba(14,73,97,0.11)]">
                                <p className="text-sm font-extrabold text-[#0E4961]">
                                    No checklist available right now
                                </p>
                                <p className="text-xs text-[#6E8794] mt-1">
                                    Checklists appear only inside their submission window.
                                </p>
                            </div>
                        ) : (
                            checklistGroups.map((group) => (
                                <div key={group.id}>
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xl">{group.icon}</span>
                                            <div>
                                                <h3 className="text-[#0E4961] font-extrabold text-lg leading-tight">
                                                    {group.groupTitle}
                                                </h3>
                                                <p className="text-xs text-[#6E8794] font-medium">
                                                    {group.groupSubtitle}
                                                </p>
                                            </div>
                                        </div>
                                        <span className="rounded-full bg-white border border-[#DDF0F6] px-3 py-1 text-[10px] font-extrabold text-[#0E4961]">
                                            {group.items.length}
                                        </span>
                                    </div>

                                    <div className="space-y-4">
                                        {group.items.map((item) => {
                                            const statusConfig =
                                                STATUS_CONFIG[item.status] ||
                                                STATUS_CONFIG.PENDING;
                                            const isMissed = item.status === "MISSED";
                                            const isViewOnly =
                                                item.readOnly || item.status === "SUBMITTED";

                                            const card = (
                                                <>
                                                    <div
                                                        className={`absolute -right-10 -top-10 w-32 h-32 rounded-full blur-xl ${statusConfig.decorationClass}`}
                                                    />
                                                    <div className="relative flex items-center gap-3">
                                                        <StatusIndicator
                                                            status={item.status}
                                                            taskCount={item.taskCount}
                                                        />
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex flex-wrap items-center gap-2">
                                                                <h4 className="text-[#0E4961] font-extrabold text-[14px] leading-tight">
                                                                    {item.title}
                                                                </h4>
                                                                <span
                                                                    className={`rounded-full px-2 py-0.5 text-[9px] font-black ${statusConfig.badgeClass}`}
                                                                >
                                                                    {statusConfig.label}
                                                                </span>
                                                                {item.readOnly && item.status !== "MISSED" && (
                                                                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[9px] font-black text-slate-500">
                                                                        READ ONLY
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p
                                                                className={`text-[11px] font-bold mt-1.5 leading-relaxed ${statusConfig.statusTextClass}`}
                                                            >
                                                                {getChecklistStatusMessage(item)}
                                                            </p>
                                                            {/* <div className="flex flex-wrap items-center gap-2 mt-3">
                                                                <span className="rounded-full bg-white/80 border border-[#DDF0F6] px-3 py-1 text-[10px] font-bold text-[#6E8794]">
                                                                    {FREQUENCY_HINT[item.frequency] ||
                                                                        labelize(item.frequency)}
                                                                </span>
                                                                <span className="rounded-full bg-white/80 border border-[#DDF0F6] px-3 py-1 text-[10px] font-bold text-[#6E8794]">
                                                                    {item.taskCount} tasks
                                                                </span>
                                                                {item.businessDate && (
                                                                    <span className="rounded-full bg-white/80 border border-[#DDF0F6] px-3 py-1 text-[10px] font-bold text-[#6E8794]">
                                                                        {formatBusinessDate(item.businessDate)}
                                                                    </span>
                                                                )}
                                                            </div> */}
                                                        </div>
                                                        {!isMissed && (
                                                            <span
                                                                aria-hidden="true"
                                                                className="shrink-0 inline-flex text-[#0E4961]"
                                                            >
                                                                <svg
                                                                    viewBox="0 0 24 24"
                                                                    className="w-5 h-5"
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    strokeWidth="1.8"
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                >
                                                                    <path d="M9 5.5l6.5 6.5L9 18.5" />
                                                                </svg>
                                                            </span>
                                                        )}
                                                    </div>
                                                </>
                                            );

                                            if (isMissed) {
                                                return (
                                                    <div
                                                        key={`${group.id}-${item.categoryId}-${item.businessDate}`}
                                                        aria-disabled="true"
                                                        className={`relative w-full overflow-hidden rounded-[30px] p-4 text-left cursor-not-allowed ${statusConfig.cardClass}`}
                                                    >
                                                        {card}
                                                    </div>
                                                );
                                            }

                                            return (
                                                <button
                                                    type="button"
                                                    key={`${group.id}-${item.categoryId}-${item.businessDate}`}
                                                    onClick={() => openChecklist(item)}
                                                    aria-label={`${isViewOnly ? "View" : item.status === "DRAFT" ? "Continue" : "Start"} ${item.title}`}
                                                    className={`group relative w-full overflow-hidden rounded-[30px] p-4 text-left transition-all duration-300 active:scale-[0.985] hover:-translate-y-0.5 ${statusConfig.cardClass}`}
                                                >
                                                    {card}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
                </PullToRefresh>
            </section>
        </main>
    );
}
